import React, { useState, useRef } from 'react';
import './FileUpload.css';

const FileUpload = ({ 
  entityType, 
  entityId, 
  onUploadSuccess, 
  onUploadError,
  multiple = false 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ];

  const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png'];

  const validateFile = (file) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 10MB limit';
    }

    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return 'File type not allowed. Allowed types: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG';
    }

    if (file.type && !ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'File type not allowed. Allowed types: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG';
    }

    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      if (onUploadError) {
        onUploadError(validationError);
      }
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = sessionStorage.getItem('token');
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          setUploading(false);
          setUploadProgress(0);
          if (onUploadSuccess) {
            onUploadSuccess(response.data);
          }
        } else {
          const errorResponse = JSON.parse(xhr.responseText);
          const errorMessage = errorResponse.error?.message || 'Upload failed';
          setError(errorMessage);
          setUploading(false);
          setUploadProgress(0);
          if (onUploadError) {
            onUploadError(errorMessage);
          }
        }
      });

      xhr.addEventListener('error', () => {
        const errorMessage = 'Network error during upload';
        setError(errorMessage);
        setUploading(false);
        setUploadProgress(0);
        if (onUploadError) {
          onUploadError(errorMessage);
        }
      });

      xhr.open('POST', `http://localhost:8080/api/v1/${entityType.toLowerCase()}s/${entityId}/documents`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    } catch (err) {
      const errorMessage = err.message || 'Upload failed';
      setError(errorMessage);
      setUploading(false);
      setUploadProgress(0);
      if (onUploadError) {
        onUploadError(errorMessage);
      }
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (multiple) {
        files.forEach(file => uploadFile(file));
      } else {
        uploadFile(files[0]);
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      if (multiple) {
        files.forEach(file => uploadFile(file));
      } else {
        uploadFile(files[0]);
      }
    }
    // Reset input
    e.target.value = '';
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="file-upload-container">
      <div
        className={`file-upload-dropzone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          multiple={multiple}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
        />
        
        {uploading ? (
          <div className="upload-progress">
            <div className="progress-icon">📤</div>
            <div className="progress-text">Uploading...</div>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <div className="progress-percentage">{uploadProgress}%</div>
          </div>
        ) : (
          <>
            <div className="upload-icon">📁</div>
            <div className="upload-text">
              <strong>Click to upload</strong> or drag and drop
            </div>
            <div className="upload-hint">
              PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 10MB)
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="file-upload-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}
    </div>
  );
};

export default FileUpload;

